<template>
  <div>
    <Dialog v-model:visible="visible" modal :closable="false" header="Export Documents" :style="{ width: '50vw' }" :breakpoints="{ '1199px': '75vw', '575px': '90vw' }">
      <div class="flex flex-col gap-2">
        <label for="username"
          >Please confirm if you want to export <strong>{{ documentsToExport.length }}</strong> documents:</label
        >
        <div class="rounded border bg-slate-100 pt-2 pb-4 px-2">
          <ScrollPanel
            class="w-full h-[150px] font-source font-medium"
            :pt="{
              wrapper: {
                style: { 'border-right': '10px solid var(--surface-ground)' },
              },
              bary: 'hover:bg-violet-400 bg-violet-300 opacity-100',
            }"
          >
            <div v-for="doc in documentsToExport" :key="doc.id">{{ doc.name }}</div>
          </ScrollPanel>
        </div>
      </div>
      <div class="mt-4">
        <small class="min-h-6 block">{{ progressText }} </small>
        <ProgressBar :mode="progressMode" style="height: 6px"></ProgressBar>
      </div>
      <template #footer>
        <Button label="Cancel" severity="secondary" @click="cancelExport" autofocus />
        <Button label="Export" :disabled="showProgress" severity="primary" @click="exportKbDocuments" autofocus />
      </template>
    </Dialog>
  </div>
</template>

<script setup>
  import { useKbStore } from '@/store/KbStore';
  import { useToast } from 'vue-toastification';
  import { ref, computed } from 'vue';
  import Dialog from 'primevue/dialog';
  import Button from 'primevue/button';
  import ProgressBar from 'primevue/progressbar';
  import ScrollPanel from 'primevue/scrollpanel';
  import { useDownload } from '@/hooks/useDownload';

  const kbStore = useKbStore();
  const toast = useToast();
  const visible = ref(false);
  const showProgress = ref(false);
  const progressMode = computed(() => (showProgress.value ? 'indeterminate' : ''));
  const progressText = ref('');
  const documentsToExport = ref([]);
  const download = useDownload();

  let exportCancelled = false;

  function start(documents) {
    documentsToExport.value = documents;
    visible.value = true;
  }

  async function exportKbDocuments() {
    exportCancelled = false;
    showProgress.value = true;

    const docIds = documentsToExport.value.map((doc) => doc.id);
    const exportResult = await kbStore.exportKbDocuments(docIds);
    if (exportCancelled) {
      toast.info(`Export operation has been cancelled`);
      showProgress.value = false;
      visible.value = false;
      return;
    }

    if (!exportResult.success) {
      toast.error(`Error exporting documents: ${exportResult.error}`);
      showProgress.value = false;
      visible.value = false;
      return;
    }

    downloadExportData(exportResult.data, exportResult.mimeType);
    toast.info(`Export result: exported:${exportResult.info.exported} failed:${exportResult.info.failed}`);

    kbStore.selectedKbDocuments = [];
    progressText.value = '';
    showProgress.value = false;
    visible.value = false;
  }

  function downloadExportData(data, mimeType) {
    const extension = mimeType === 'application/gzip' ? 'gz' : 'json';

    // Format as YYYYMMDD_HHMMSS
    const now = new Date();
    const timestamp = now
      .toISOString()
      .replace(/[-:]/g, '') // Remove dashes and colons
      .replace('T', '_') // Replace T with underscore
      .slice(0, 15); // Take only YYYYMMDD_HHMMSS part

    const fileName = `kb_${kbStore.kbId}_export_${timestamp}.${extension}`;
    if (mimeType === 'application/gzip') {
      download.downloadBinaryFile(fileName, data, mimeType);
    } else {
      download.downloadFile(fileName, data, mimeType);
    }
  }

  async function cancelExport() {
    if (!showProgress.value) {
      visible.value = false;
      return;
    }
    exportCancelled = true;
  }

  defineExpose({ start });
</script>
